import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Minus, Plus, ShoppingBag, Check, Truck, Shield } from "lucide-react";
import { toast } from "sonner";
import { COLOURS, SIZES, PRICE, PRODUCT_NAME, colourById, EASE } from "@/lib/brand";
import { useCart } from "@/context/CartContext";
import { Reveal, Eyebrow, MaskLine } from "./Reveal";

const Stage = ({ colour, view, setView }) => {
  const c = colourById(colour);
  const src = view === "product" ? c.product : c.model;
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--x", `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty("--y", `${e.clientY - r.top}px`);
  };
  return (
    <div className="relative">
      <div onMouseMove={onMove} data-testid="studio-stage" className="spotlight relative aspect-square overflow-hidden rounded-[28px] border border-white/10 bg-[radial-gradient(ellipse_at_50%_0%,#1a2333_0%,#07090D_70%)]">
        <AnimatePresence initial={false}>
          <motion.img
            key={src}
            src={src}
            alt={`${c.label} MenTellHealth hoodie`}
            initial={{ opacity: 0, scale: 1.06 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8, ease: EASE }}
            className="absolute inset-0 h-full w-full object-cover"
          />
        </AnimatePresence>
        <span className="absolute left-4 top-4 z-10 rounded-full bg-black/60 backdrop-blur px-3 py-1.5 font-mono text-[11px] uppercase tracking-widest">{c.label} · {c.tag}</span>
      </div>
      <div className="mt-4 flex gap-3">
        {["product", "model"].map((v) => (
          <button key={v} data-testid={`studio-view-${v}`} onClick={() => setView(v)} className={`relative h-20 w-20 overflow-hidden rounded-xl border transition-[border-color,opacity] ${view === v ? "border-[var(--mth-blue)]" : "border-white/10 opacity-60 hover:opacity-100"}`}>
            <img src={v === "product" ? c.product : c.model} alt="" className="h-full w-full object-cover" />
          </button>
        ))}
      </div>
    </div>
  );
};

export const Studio = () => {
  const { add, setOpen } = useCart();
  const [colour, setColour] = useState("black");
  const [size, setSize] = useState("");
  const [qty, setQty] = useState(1);
  const [view, setView] = useState("product");

  const addToBag = (openBag) => {
    if (!size) {
      toast.error("Pick your size first");
      return;
    }
    add(colour, size, qty);
    toast.success(`${qty} × ${colourById(colour).label} / ${size} added to your bag`);
    if (openBag) setOpen(true);
  };

  return (
    <section id="shop" data-testid="studio-section" className="relative bg-[#090C11] py-24 md:py-36">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
          <div>
            <Eyebrow>02 — The hoodie</Eyebrow>
            <h2 className="mt-5 font-display font-black uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.92] tracking-tight">
              <MaskLine inView>Your story.</MaskLine>
              <MaskLine inView delay={0.1}>Your <span className="text-[var(--mth-blue)]">colour.</span></MaskLine>
            </h2>
          </div>
          <p className="max-w-sm text-slate-300">One hoodie, four colours, every adult size. One flat price — because looking after yourself shouldn't come with a size tax.</p>
        </div>

        <div className="grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <Stage colour={colour} view={view} setView={setView} />
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5">
            <div className="lg:sticky lg:top-32">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-slate-400">Drop 01 · Unisex adult fit</p>
              <h3 data-testid="product-name" className="mt-3 font-display font-black text-3xl sm:text-4xl uppercase">{PRODUCT_NAME}</h3>
              <p data-testid="product-price" className="mt-3 font-display font-extrabold text-4xl text-[var(--mth-blue)]">£{PRICE}<span className="ml-2 align-middle font-mono text-xs uppercase tracking-widest text-slate-400">all sizes</span></p>

              <div className="mt-10">
                <p className="font-mono text-xs uppercase tracking-[0.22em] text-slate-300 mb-4">Colour — <span className="text-white">{colourById(colour).label}</span></p>
                <div className="flex gap-3">
                  {COLOURS.map((c) => (
                    <button key={c.id} data-testid={`colour-picker-${c.id}`} aria-label={c.label} onClick={() => setColour(c.id)} className={`relative h-12 w-12 rounded-full transition-transform hover:scale-110 ${colour === c.id ? "ring-2 ring-[var(--mth-blue)] ring-offset-4 ring-offset-[#090C11]" : "ring-1 ring-white/20"}`} style={{ background: c.swatch }}>
                      {colour === c.id && <Check className={`absolute inset-0 m-auto h-4 w-4 ${c.id === "white" || c.id === "grey" ? "text-black" : "text-white"}`} />}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-8">
                <p className="font-mono text-xs uppercase tracking-[0.22em] text-slate-300 mb-4">Size — <span className="text-white">{size || "Select"}</span></p>
                <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
                  {SIZES.map((s) => (
                    <button key={s} data-testid={`size-option-${s}`} onClick={() => setSize(s)} className={`h-12 rounded-xl border font-mono text-sm transition-[background-color,border-color,color] ${size === s ? "bg-white text-black border-white" : "border-white/15 hover:border-white/50"}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-8 flex items-center gap-3">
                <div className="flex items-center h-14 rounded-full border border-white/15">
                  <button data-testid="qty-decrease" onClick={() => setQty((q) => Math.max(1, q - 1))} className="h-14 w-12 grid place-items-center hover:text-[var(--mth-blue)]"><Minus className="h-4 w-4" /></button>
                  <span data-testid="qty-value" className="w-8 text-center font-mono">{qty}</span>
                  <button data-testid="qty-increase" onClick={() => setQty((q) => Math.min(20, q + 1))} className="h-14 w-12 grid place-items-center hover:text-[var(--mth-blue)]"><Plus className="h-4 w-4" /></button>
                </div>
                <button data-testid="add-to-cart-button" onClick={() => addToBag(false)} className="flex-1 h-14 rounded-full bg-white text-black font-display font-bold uppercase tracking-wider flex items-center justify-center gap-2 hover:bg-[var(--mth-blue)] hover:text-white transition-colors">
                  <ShoppingBag className="h-4 w-4" /> Add to bag
                </button>
              </div>
              <button data-testid="buy-now-button" onClick={() => addToBag(true)} className="glow-btn mt-3 w-full justify-center h-14">Buy now — £{PRICE * qty}</button>

              <ul className="mt-10 space-y-3 text-sm text-slate-300">
                <li className="flex gap-3"><Truck className="h-4 w-4 text-[var(--mth-blue)] shrink-0 mt-0.5" /> UK delivery — address collected securely at checkout</li>
                <li className="flex gap-3"><Shield className="h-4 w-4 text-[var(--mth-blue)] shrink-0 mt-0.5" /> Secure card, Apple Pay & Google Pay payments via Stripe</li>
              </ul>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
